import React, { useState } from 'react';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';

const formatDate = (date) => {
  if (!date) return 'N/A';
  // Create date object and adjust for timezone
  const dateObj = new Date(date);
  const timezoneOffset = dateObj.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
  const adjustedDate = new Date(dateObj.getTime() + timezoneOffset);
  
  return adjustedDate.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    timeZone: 'UTC' // Force UTC to prevent double timezone conversion
  });
};

export default function PlaybookCard({ 
  playbook, 
  isActive, 
  onClick, 
  onView, 
  onDelete,
  onRefresh,
  deleteConfirm, 
  showActions = false,
  isRefreshing = false
}) {
  const [refreshConfirm, setRefreshConfirm] = useState(null);
  
  // Check if event date is older than today minus 1 day
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const isEventOld = playbook.event_date ? new Date(playbook.event_date) < yesterday : false;
  
  // Check if last updated is older than today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lastUpdated = playbook.last_updated ? new Date(playbook.last_updated) : null;
  const needsUpdate = lastUpdated ? lastUpdated < today : false;
  
  // Show refresh button if either condition is met
  const showRefresh = isEventOld || needsUpdate;

  // Handle refresh click with confirmation
  const handleRefreshClick = (e) => {
    e.stopPropagation();
    if (refreshConfirm === playbook.id) {
      onRefresh?.(playbook.id);
      setRefreshConfirm(null);
    } else {
      setRefreshConfirm(playbook.id);
    }
  };

  return (
    <div 
      className={`playbook-card ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      <div className="playbook-header">
        <div>
          <h3 className="playbook-title">{playbook.name || 'Untitled Playbook'}</h3>
          {playbook.event && (
            <div className="playbook-event">
              <EventIcon sx={{ fontSize: 16 }} />
              {playbook.event.name}
            </div>
          )}
        </div>
        {showActions && (
          <button 
            className="view-icon" 
            onClick={(e) => {
              e.stopPropagation();
              onView(playbook.id);
            }}
            disabled={isRefreshing}
          >
            <VisibilityIcon sx={{ fontSize: 20 }} />
          </button>
        )}
      </div>

      <div className="playbook-stats">
        <div className="stat-row">
          <span className="stat-label">Event:</span>
          <span className="stat-value">{playbook.event_name || 'No event'}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Event Date:</span>
          <span className="stat-value">{formatDate(playbook.event_date)}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Updated:</span>
          <span className="stat-value">{formatDate(playbook.last_updated)}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Picks:</span>
          <span className="stat-value">{playbook.picks?.length || 0}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Returns:</span>
          <span className="stat-value">{(playbook.portfolio_returns * 100).toFixed(2)}%</span>
        </div>
      </div>

      {showActions && (
        <div className="playbook-actions">
          {showRefresh && (
            <button 
              className={`action-button refresh-button ${refreshConfirm === playbook.id ? 'confirm' : ''}`}
              onClick={handleRefreshClick}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <CircularProgress size={20} color="inherit" /> Refreshing...
                </>
              ) : refreshConfirm === playbook.id ? (
                <>
                  <WarningIcon /> Confirm Refresh
                </>
              ) : (
                <>
                  <RefreshIcon /> Refresh Picks
                </>
              )}
            </button>
          )}
          <button 
            className={`action-button delete-button ${deleteConfirm === playbook.id ? 'confirm' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(playbook.id);
            }}
            disabled={isRefreshing}
          >
            {deleteConfirm === playbook.id ? (
              <>
                <WarningIcon /> Confirm Delete
              </>
            ) : (
              <>
                <DeleteIcon /> Delete
              </>
            )}
          </button>
        </div>
      )}

      <style jsx>{`
        .playbook-card {
          background: var(--surface-light);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(123, 73, 209, 0.15);
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .playbook-card:hover {
          border-color: rgba(123, 73, 209, 0.4);
          background: var(--surface-light);
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .playbook-card.active {
          border-color: var(--primary);
          background: rgba(123, 73, 209, 0.1);
        }

        .playbook-header {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(123, 73, 209, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .playbook-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: white;
          letter-spacing: -0.01em;
        }

        .playbook-event {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.5);
          margin-top: 8px;
          padding: 6px 12px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 20px;
          width: fit-content;
        }

        .view-icon {
          background: transparent;
          border: none;
          padding: 8px;
          border-radius: 50%;
          cursor: pointer;
          color: rgba(255, 255, 255, 0.6);
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .view-icon:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .view-icon:not(:disabled):hover {
          color: white;
          background: rgba(123, 73, 209, 0.1);
          transform: translateY(-2px);
        }

        .playbook-stats {
          display: grid;
          gap: 12px;
          margin-bottom: ${showActions ? '20px' : '0'};
        }

        .stat-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 13px;
          border: 1px solid rgba(123, 73, 209, 0.1);
          transition: all 0.2s ease;
        }

        .stat-row:hover {
          border-color: rgba(123, 73, 209, 0.2);
          background: rgba(123, 73, 209, 0.03);
        }

        .stat-label {
          color: rgba(255, 255, 255, 0.6);
        }

        .stat-value {
          color: white;
          font-weight: 500;
        }

        .playbook-actions {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid rgba(123, 73, 209, 0.1);
        }

        .action-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .view-button {
          background: var(--primary);
          color: white;
          border: none;
          box-shadow: 0 4px 12px rgba(123, 73, 209, 0.2);
        }

        .view-button:hover {
          background: #8b59e1;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(123, 73, 209, 0.3);
        }

        .delete-button {
          background: transparent;
          color: #ff4d4d;
          border: 1px solid currentColor;
        }

        .delete-button:disabled {
          opacity: 0.5;
          cursor: wait;
          border-color: rgba(255, 77, 77, 0.5);
          color: rgba(255, 77, 77, 0.5);
        }

        .delete-button:not(:disabled):hover {
          background: rgba(255, 77, 77, 0.05);
          transform: translateY(-2px);
        }

        .delete-button.confirm {
          background: #ff4d4d;
          color: white;
          border: none;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 77, 77, 0.4); }
          70% { box-shadow: 0 0 0 6px rgba(255, 77, 77, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 77, 77, 0); }
        }

        .refresh-button {
          background: #2196f3;
          color: white;
          border: none;
          opacity: 1;
          transition: all 0.2s ease;
        }

        .refresh-button:disabled {
          opacity: 0.7;
          cursor: wait;
        }

        .refresh-button:not(:disabled):hover {
          background: #1976d2;
          transform: translateY(-2px);
        }

        @media (min-width: 640px) {
          .playbook-actions {
            grid-template-columns: repeat(${showRefresh ? '2' : '1'}, 1fr);
          }
        }
      `}</style>
    </div>
  );
} 